// NovelImageDragInterface.tsx
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Game.css";

// --- ストーリーシーケンス ---
type StoryItem = {
    story: string;
    timeout: number;
}
type NeoStoryItem = {
    story: string;
    timeout: number;
    answerIndex: number;
    correctIndex: number;
    correctImage: string;
}
type BranchItem = {
    image: string;
    answerIndex: number;
    correctImage: string;
    incorrectIndex: number;
};

type NeoBranchItem = {
    image: string;
    answerIndex: number;
    correctImage: string;
    correctIndex: number;
    incorrectIndex: number;
};

type EndingItem = {
    ending: string;
    isCorrect: boolean;
};

const textSequence: (StoryItem | NeoStoryItem | BranchItem | NeoBranchItem | EndingItem)[] = [
    { story: "ねえ、", timeout: 3000 },
    { story: "私のこと、「好き」？", timeout: 3000 },
    { image: "/apr2025/images/nazo1.png", answerIndex: 0, correctImage: "/apr2025/images/ans1.png", incorrectIndex: 25},
    { story: "もっと言ってほしい", timeout: 3000 },
    { image: "/apr2025/images/nazo2.png", answerIndex: 3, correctImage: "/apr2025/images/ans2.png", incorrectIndex: 25 },

    { story: "じゃあ、", timeout: 3000 },
    { story: "どれくらい好き？", timeout: 3000 },
    { image: "/apr2025/images/nazo4.png", answerIndex: 1, correctImage: "/apr2025/images/ans4.png", incorrectIndex: 25 },
    { story: "ほんとにそう思ってる？", timeout: 3000 },
    { story: "じゃあ、", timeout: 3000 },

    { story: "わたしのどこが好き？", timeout: 3000 },
    { image: "/apr2025/images/nazo5.png", answerIndex: 0, correctImage: "/apr2025/images/ans5.png", incorrectIndex: 25 },
    { story: "なに全部って", timeout: 3000 },
    { story: "適当なこと言わないでよ", timeout: 3000 },
    { story: "ほんとに私のこと好き？", timeout: 3000 },

    { image: "/apr2025/images/nazo3.png", answerIndex: 2, correctImage: "/apr2025/images/ans3.png", incorrectIndex: 25 },
    { story: "ほんとに？", timeout: 3000 },
    { story: "どうせ", timeout: 3000 },
    { story: "口だけ", timeout: 3000, answerIndex: 3, correctIndex: 22, correctImage: "/apr2025/images/ans6.png" },
    { story: "なんでしょ？", timeout: 3000 },

    { story: "ほんとに私のこと好き？", timeout: 3000 },
    { image: "/apr2025/images/nazo3.png", answerIndex: 2, correctImage: "/apr2025/images/ans3.png", correctIndex: 16, incorrectIndex: 25 },
    { story: "……！？", timeout: 4000 },
    { story: "…………", timeout: 4000 },
    { ending: "俺「うるせぇ口だな……」", isCorrect: true },

    { story: "なに？", timeout: 3000 },
    { story: "何言ってるか全然わからないよ", timeout: 3000 },
    { ending: "間違っていたみたいだ", isCorrect: false },
];

const bottomImages = [
    "/apr2025/images/icon1.png",
    "/apr2025/images/icon2.png",
    "/apr2025/images/icon3.png",
    "/apr2025/images/icon4.png",
];

export default function NovelImageDragInterface() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showCorrectImage, setShowCorrectImage] = useState(false);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const upperAreaRef = useRef<HTMLDivElement>(null);

    let timer: NodeJS.Timeout | null = null;

    const goNextPage = () => {
        if (!showCorrectImage) {
            setCurrentIndex((prev) => Math.min(prev + 1, textSequence.length - 1));
        }
    }

    useEffect(() => {
        const currentItem = textSequence[currentIndex];
        if ("story" in currentItem) {
            timer = setTimeout(() => {
                goNextPage();
            }, currentItem.timeout);
            return () => {
                if (timer !== null) {
                    clearTimeout(timer);
                }
            }
        }
    }, [currentIndex]);

    const handleButtonClick = () => {
        setCurrentIndex((prev) => Math.min(0, textSequence.length - 1));
    };

    const handleDrop = (index: number) => {
        const currentItem = textSequence[currentIndex];

        if ("answerIndex" in currentItem) {
            if (timer !== null) {
                clearTimeout(timer);
            }

            if (index === currentItem.answerIndex) {
                setShowCorrectImage(true); // ✅ 一時的に正解画像を表示
                if ("correctIndex" in currentItem) {
                    setTimeout(() => {
                        setShowCorrectImage(false);
                        setCurrentIndex((prev) => Math.min(currentItem.correctIndex, textSequence.length - 1));
                    }, 1500); // ✅ 800ms 待ってから次のストーリーへ
                }
                else {
                    setTimeout(() => {
                        setShowCorrectImage(false);
                        setCurrentIndex((prev) => Math.min(prev + 1, textSequence.length - 1));
                    }, 1500); // ✅ 800ms 待ってから次のストーリーへ
                }
            }
            else if ("incorrectIndex" in currentItem) {
                setCurrentIndex((prev) => Math.min(currentItem.incorrectIndex, textSequence.length - 1));
            }
        }
        setDraggingIndex(null);
        setIsDragging(false);
    };

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
        setDragPosition({ x: e.clientX, y: e.clientY });
    };

    const handleTouchStart = (index: number, e: React.TouchEvent) => {
        setDraggingIndex(index);
        setIsDragging(true);
        setDragPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return;
        setDragPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    };

    const handleTouchEnd = () => {
        if (!isDragging || draggingIndex === null || !upperAreaRef.current) return;

        const targetRect = upperAreaRef.current.getBoundingClientRect();
        const x = dragPosition.x;
        const y = dragPosition.y;

        if (x >= targetRect.left && x <= targetRect.right && y >= targetRect.top && y <= targetRect.bottom) {
            handleDrop(draggingIndex);
        } else {
            setDraggingIndex(null);
            setIsDragging(false);
        }
    };

    const currentItem = textSequence[currentIndex];

    return (
        <div className="novel-container">
            <div className="novel-upper" ref={upperAreaRef}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(parseInt(e.dataTransfer.getData("index")))}>
                <AnimatePresence mode="wait">
                    {"story" in currentItem ? (
                        (showCorrectImage && "correctImage" in currentItem) ? (
                            <motion.img
                                key={`correct-${currentIndex}`}
                                src={currentItem.correctImage}
                                className="novel-question-image correct-image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            />
                        ) : (
                            <motion.div
                                key={currentIndex}
                                className="novel-text"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                {currentItem.story}
                            </motion.div>
                        )
                    ) : "image" in currentItem ? (
                        showCorrectImage ? (
                            <motion.img
                                key={`correct-${currentIndex}`}
                                src={currentItem.correctImage}
                                className="novel-question-image correct-image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            />
                        ) : (
                            <motion.img
                                key={`img-${currentIndex}`}
                                src={currentItem.image}
                                className="novel-question-image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            />
                        )
                    ) : "ending" in currentItem ? (
                        <motion.div
                            key={`end-${currentIndex}`}
                            className="ending-screen"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.2 }}
                                >
                            {currentItem.isCorrect ? (
                                <>
                                <div className="clear">{currentItem.ending}</div>
                                            <a href={`https://x.com/intent/tweet?text=%E2%96%A0%E2%96%A0%E2%96%A0%E2%96%A0%E2%96%A0%0D%0A%E9%BB%99%E3%82%89%E3%81%9B%E3%81%9F%E3%80%82%0D%0A%E2%96%A0%E2%96%A0%E2%96%A0%E2%96%A0%E2%96%A0%0D%0A%0D%0Ahttps%3A%2F%2Fridkmy.github.io%2Fapr2025%2F%0D%0A%0D%0A%23%E8%AC%8E%E8%A7%A3%E3%81%8D+%23%E9%9D%A2%E5%80%92%E8%AC%8E+%40ridkmy`} target="_blank" rel="noopener noreferrer" className="post-button">
                                        Xでポストする
                                    </a>
                                    <div>
                                                <a href="https://note.com/deko613/n/nd6c593102c1a" className="other-button" target="_blank" rel="noopener noreferrer" >
                                            あとがきはこちら
                                        </a>
                                    </div>
                                    <div>
                                        <a href="https://ridkmy.github.io/" className="other-button" target="_blank" rel="noopener noreferrer" >
                                            作者の他作品はこちら
                                        </a>
                                    </div>
                                </>
                                    ) : (
                                <>

                                    <div className="clear">{currentItem.ending}</div>
                                    <button
                                        className="retry-button"
                                        onClick={() => handleButtonClick()}
                                    >
                                        やりなおす
                                    </button>
                                </>
                            )}
                        </motion.div>
                    ) : null}
                </AnimatePresence>

                {isDragging && draggingIndex !== null && (
                    <div
                        className="drag-preview"
                        style={{ top: dragPosition.y, left: dragPosition.x }}
                    >
                        <img src={bottomImages[draggingIndex]} alt="drag-preview" className="drag-image" />
                    </div>
                )}
            </div>

            {!(typeof currentItem !== "string" && "ending" in currentItem) && (
                <div className="novel-bottom">
                    {bottomImages.map((img, index) => (
                        <div
                            key={index}
                            className="bottom-image-wrapper"
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData("index", index.toString());
                                setDraggingIndex(index);
                                setIsDragging(true);
                            }}
                            onDragEnd={() => {
                                setDraggingIndex(null);
                                setIsDragging(false);
                            }}
                            onDrag={(e) => handleDrag(e)}
                            onTouchStart={(e) => handleTouchStart(index, e)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <img src={img} className="bottom-image" alt={`choice-${index}`} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}